import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { saveLocalData } from "../../helpers/storage-helper";
import { endPoints } from "../../config/end-points";
import useHttp from "../../hooks/use-http";
import { cuteToast } from "../UI/cute";
import { access_specifier } from "../../config/app.config";
import "./userprofile.css";

const UserProfile = () => {
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    access: [],
    app_all_access: [],
    upgrade_access: [],
  });

  const email = useSelector((state) => state.auth.email);
  const first_name = useSelector((state) => state.auth.first_name);
  const last_name = useSelector((state) => state.auth.last_name);
  const myAccess = useSelector((state) => state.auth.access);
  const { sendRequest } = useHttp();
  const [showProfile, setShowProfile] = useState(true);

  useEffect(() => {
    document.title = `${endPoints.title} | Profile`;
  }, []);

  useEffect(() => {
    const have_access = myAccess ? myAccess.split(",").filter((item) => item !== "5" && item !== "") : [];
    const app_all_access = Object.keys(access_specifier).filter((key) => key !== "5");
    const upgrade_access = app_all_access.filter((x) => !have_access.includes(x));

    setProfile({
      first_name,
      last_name,
      email,
      password: "",
      access: have_access,
      app_all_access,
      upgrade_access,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendRequest(
      { url: endPoints.updateprofile, body: profile, method: "POST" },
      (response) => {
        if (response.id) {
          cuteToast({ type: "success", message: "Successfully updated", timer: 3000, title: "Profile Updated" });
          saveLocalData("sitebuilder_user", response);
        } else {
          cuteToast({ type: "error", message: "Something went wrong!", timer: 3000, title: response.message });
        }
      }
    );
  };

  const getInitials = () => {
    const f = profile.first_name?.[0] || "";
    const l = profile.last_name?.[0] || "";
    return (f + l).toUpperCase() || "?";
  };

  if (!showProfile) {
    return (
      <section className="prof-page">
        <div className="container">
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <h2>Forbidden Access</h2>
            <p>You don't have permission to access this page.</p>
            <Link to="/" style={{ color: "var(--bland-purple-600)" }}>Go Home</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="prof-page">
      <div className="container">
        {/* Header */}
        <div className="prof-header">
          <h2>My Profile</h2>
          <p>Manage your account details and view your access</p>
        </div>

        {/* Two-column layout */}
        <div className="prof-grid">
          {/* Left — Update Profile */}
          <div className="prof-card">
            <div className="prof-card-header">
              <i className="bi bi-person-circle"></i>
              <h5>Update Profile</h5>
            </div>
            <div className="prof-card-body">
              {/* Avatar block */}
              <div className="prof-avatar-block">
                <div className="prof-avatar">{getInitials()}</div>
                <p className="prof-avatar-name">
                  {profile.first_name} {profile.last_name}
                </p>
                <span className="prof-avatar-email">{profile.email}</span>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="prof-field">
                  <label className="prof-field-label">First Name</label>
                  <input
                    type="text"
                    className="prof-field-input"
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value.trim() })}
                  />
                </div>

                <div className="prof-field">
                  <label className="prof-field-label">Last Name</label>
                  <input
                    type="text"
                    className="prof-field-input"
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                  />
                </div>

                <div className="prof-field">
                  <label className="prof-field-label">Password</label>
                  <input
                    type="password"
                    className="prof-field-input"
                    value={profile.password}
                    onChange={(e) => setProfile({ ...profile, password: e.target.value.trim() })}
                    placeholder="Leave blank to keep current"
                  />
                  <span className="prof-field-hint">Only fill if you want to change your password</span>
                </div>

                <button type="submit" className="prof-submit">
                  <i className="bi bi-check-lg"></i>
                  Update Profile
                </button>
              </form>
            </div>
          </div>

          {/* Right — Access */}
          <div className="prof-card">
            <div className="prof-card-header">
              <i className="bi bi-shield-check"></i>
              <h5>My Access</h5>
            </div>
            <div className="prof-card-body">
              {/* Current Access */}
              <p className="prof-access-label">
                <i className="bi bi-check-circle-fill" style={{ color: "var(--bland-success)" }}></i>
                Current Access
              </p>
              <div className="prof-access-grid">
                {profile.access.map((item, index) => (
                  <div className="prof-access-chip prof-access-chip--active" key={index}>
                    <div className="prof-access-icon prof-access-icon--active">
                      <i className="bi bi-check-lg"></i>
                    </div>
                    <span className="prof-access-name">{access_specifier[item]}</span>
                  </div>
                ))}
              </div>

              {/* Upgrade Access */}
              {profile.upgrade_access.length > 0 && (
                <>
                  <p className="prof-access-label">
                    <i className="bi bi-lock" style={{ color: "var(--bland-gray-400)" }}></i>
                    Available to Upgrade
                  </p>
                  <div className="prof-access-grid">
                    {profile.upgrade_access.map((item, index) => (
                      <div className="prof-access-chip prof-access-chip--locked" key={index}>
                        <div className="prof-access-icon prof-access-icon--locked">
                          <i className="bi bi-lock"></i>
                        </div>
                        <span className="prof-access-name">{access_specifier[item]}</span>
                        <a
                          href={`${process.env.PUBLIC_URL}/upgrade-access/${btoa(item)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="prof-upgrade-btn"
                        >
                          Upgrade
                        </a>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserProfile;
